import {
  nearestPdfFontWeight,
  PDF_DEFAULT_FONT,
  PDF_FONT_CATEGORIES,
  PDF_FONT_SIZES,
  type PdfFontCategory,
  type PdfTemplate,
  pdfFontWeights,
  TABLE_STYLE_TYPES,
} from "@repo/shared/types";
import { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdatePdfTemplateStyle } from "@/lib/hooks/use-pdf-templates";
import { useT } from "@/lib/i18n/use-t";
import { type HeadingStyleValue, PdfTemplateHeadingStyle } from "./pdf-template-heading-style";
import { T } from "./pdf-template-style.i18n";

const FONT_CATEGORY_ORDER: PdfFontCategory[] = ["Default", "Sans Serif", "Serif", "Monospace"];

type PdfTemplateStyleProps = {
  template: Pick<
    PdfTemplate,
    | "id"
    | "fontFamily"
    | "fontSize"
    | "tableHeaderBgColor"
    | "tableHeaderTextColor"
    | "tableStyleType"
    | "tableAlternatingColor"
    | "headingFontSize"
    | "headingFontWeight"
    | "headingColor"
    | "headingNumbered"
  >;
};

export function PdfTemplateStyle({ template }: PdfTemplateStyleProps) {
  const [error, setError] = useState<string | null>(null);
  const updateStyleMutation = useUpdatePdfTemplateStyle();
  const t = useT(T);
  const [formData, setFormData] = useState({
    fontFamily: template.fontFamily || "",
    fontSize: template.fontSize?.toString() || "",
    tableHeaderBgColor: template.tableHeaderBgColor || "",
    tableHeaderTextColor: template.tableHeaderTextColor || "",
    tableStyleType: template.tableStyleType || "borders",
    tableAlternatingColor: template.tableAlternatingColor || "",
  });
  const [heading, setHeading] = useState<HeadingStyleValue>({
    headingFontSize: template.headingFontSize?.toString() || "",
    headingFontWeight: template.headingFontWeight?.toString() || "",
    headingColor: template.headingColor || "",
    headingNumbered: template.headingNumbered !== false,
  });
  const [weightAdjustment, setWeightAdjustment] = useState<{ from: number; to: number } | null>(
    null,
  );

  // QL-384 — a font that lacks the heading weight in use moves it to the
  // nearest weight the font has, and says so. The server applies the same
  // rule on save.
  const handleFontChange = (fontFamily: string) => {
    setFormData({ ...formData, fontFamily });
    const weight = heading.headingFontWeight ? Number(heading.headingFontWeight) : null;
    if (weight !== null && !pdfFontWeights(fontFamily).includes(weight)) {
      const nearest = nearestPdfFontWeight(fontFamily, weight);
      setHeading({ ...heading, headingFontWeight: nearest.toString() });
      setWeightAdjustment({ from: weight, to: nearest });
    } else {
      setWeightAdjustment(null);
    }
  };

  const isPending = updateStyleMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await updateStyleMutation.mutateAsync({
        id: template.id,
        data: {
          fontFamily: formData.fontFamily || null,
          fontSize: formData.fontSize ? parseInt(formData.fontSize, 10) : null,
          tableHeaderBgColor: formData.tableHeaderBgColor || null,
          tableHeaderTextColor: formData.tableHeaderTextColor || null,
          tableStyleType: formData.tableStyleType || null,
          tableAlternatingColor: formData.tableAlternatingColor || null,
          headingFontSize: heading.headingFontSize ? parseInt(heading.headingFontSize, 10) : null,
          headingFontWeight: heading.headingFontWeight
            ? parseInt(heading.headingFontWeight, 10)
            : null,
          headingColor: heading.headingColor || null,
          headingNumbered: heading.headingNumbered,
        },
      });
      if (result.headingWeightAdjusted) {
        setHeading({ ...heading, headingFontWeight: result.headingWeightAdjusted.to.toString() });
        setWeightAdjustment(result.headingWeightAdjusted);
      }
      toast.success(t.saveStyle);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.failedToSave);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
      )}

      <div className="space-y-6">
        {/* Typography Section */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold">{t.typography}</h3>

          {/* Font Family */}
          <div className="space-y-2">
            <Label htmlFor="fontFamily">{t.fontFamily}</Label>
            <Select
              value={formData.fontFamily}
              onValueChange={handleFontChange}
              disabled={isPending}
            >
              <SelectTrigger id="fontFamily">
                <SelectValue placeholder={`${t.selectFont} (${PDF_DEFAULT_FONT})`} />
              </SelectTrigger>
              <SelectContent>
                {FONT_CATEGORY_ORDER.map((category, index) => (
                  <SelectGroup key={category}>
                    {index > 0 && <SelectSeparator />}
                    <SelectLabel className="bg-muted/50 text-xs font-bold uppercase tracking-wide text-muted-foreground py-2 -mx-1 px-3">
                      {category}
                    </SelectLabel>
                    {PDF_FONT_CATEGORIES[category].map((font) => (
                      <SelectItem key={font} value={font} className="pl-4">
                        {font}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Font Size */}
          <div className="space-y-2">
            <Label htmlFor="fontSize">{t.fontSize}</Label>
            <Select
              value={formData.fontSize}
              onValueChange={(value) => setFormData({ ...formData, fontSize: value })}
              disabled={isPending}
            >
              <SelectTrigger id="fontSize">
                <SelectValue placeholder={t.selectFontSize} />
              </SelectTrigger>
              <SelectContent>
                {PDF_FONT_SIZES.map((size) => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}pt
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <PdfTemplateHeadingStyle
          value={heading}
          onChange={(value) => {
            setHeading(value);
            if (value.headingFontWeight !== heading.headingFontWeight) setWeightAdjustment(null);
          }}
          fontFamily={formData.fontFamily}
          weightAdjustment={weightAdjustment}
          disabled={isPending}
        />

        {/* Table Styling Section */}
        <div className="border rounded-lg p-4 space-y-4">
          <h3 className="font-semibold">{t.tableStyling}</h3>

          {/* Table Style Type */}
          <div className="space-y-2">
            <Label htmlFor="tableStyleType">{t.tableStyleType}</Label>
            <Select
              value={formData.tableStyleType}
              onValueChange={(value) => setFormData({ ...formData, tableStyleType: value })}
              disabled={isPending}
            >
              <SelectTrigger id="tableStyleType">
                <SelectValue placeholder={t.selectTableStyle} />
              </SelectTrigger>
              <SelectContent>
                {TABLE_STYLE_TYPES.map((style) => (
                  <SelectItem key={style} value={style}>
                    {style === "borders" ? t.borders : t.alternatingRows}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table Header Background Color */}
          <div className="space-y-2">
            <Label>{t.tableHeaderBackground}</Label>
            <div className="flex items-center gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-10 h-10 rounded-lg border-2 border-input cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all shadow-sm"
                    style={{
                      backgroundColor: formData.tableHeaderBgColor || "#ffffff",
                    }}
                    disabled={isPending}
                  />
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="start">
                  <HexColorPicker
                    color={formData.tableHeaderBgColor || "#f5f5f5"}
                    onChange={(color) => setFormData({ ...formData, tableHeaderBgColor: color })}
                  />
                  <Input
                    type="text"
                    value={formData.tableHeaderBgColor}
                    onChange={(e) =>
                      setFormData({ ...formData, tableHeaderBgColor: e.target.value })
                    }
                    placeholder="#f5f5f5"
                    className="mt-2 w-full font-mono text-sm"
                    disabled={isPending}
                  />
                </PopoverContent>
              </Popover>
              <span className="font-mono text-sm text-muted-foreground">
                {formData.tableHeaderBgColor || t.none}
              </span>
              {formData.tableHeaderBgColor && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, tableHeaderBgColor: "" })}
                  disabled={isPending}
                >
                  {t.clear}
                </Button>
              )}
            </div>
          </div>

          {/* Table Header Text Color */}
          <div className="space-y-2">
            <Label>{t.tableHeaderText}</Label>
            <div className="flex items-center gap-3">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="w-10 h-10 rounded-lg border-2 border-input cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all shadow-sm"
                    style={{
                      backgroundColor: formData.tableHeaderTextColor || "#000000",
                    }}
                    disabled={isPending}
                  />
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="start">
                  <HexColorPicker
                    color={formData.tableHeaderTextColor || "#000000"}
                    onChange={(color) => setFormData({ ...formData, tableHeaderTextColor: color })}
                  />
                  <Input
                    type="text"
                    value={formData.tableHeaderTextColor}
                    onChange={(e) =>
                      setFormData({ ...formData, tableHeaderTextColor: e.target.value })
                    }
                    placeholder="#000000"
                    className="mt-2 w-full font-mono text-sm"
                    disabled={isPending}
                  />
                </PopoverContent>
              </Popover>
              <span className="font-mono text-sm text-muted-foreground">
                {formData.tableHeaderTextColor || t.noneBlack}
              </span>
              {formData.tableHeaderTextColor && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setFormData({ ...formData, tableHeaderTextColor: "" })}
                  disabled={isPending}
                >
                  {t.clear}
                </Button>
              )}
            </div>
          </div>

          {/* Alternating Row Color - only show when style is "alternating" */}
          {formData.tableStyleType === "alternating" && (
            <div className="space-y-2">
              <Label>{t.tableAlternatingRowColor}</Label>
              <div className="flex items-center gap-3">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-10 h-10 rounded-lg border-2 border-input cursor-pointer hover:ring-2 hover:ring-ring hover:ring-offset-2 transition-all shadow-sm"
                      style={{
                        backgroundColor: formData.tableAlternatingColor || "#f9f9f9",
                      }}
                      disabled={isPending}
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-3" align="start">
                    <HexColorPicker
                      color={formData.tableAlternatingColor || "#f9f9f9"}
                      onChange={(color) =>
                        setFormData({ ...formData, tableAlternatingColor: color })
                      }
                    />
                    <Input
                      type="text"
                      value={formData.tableAlternatingColor}
                      onChange={(e) =>
                        setFormData({ ...formData, tableAlternatingColor: e.target.value })
                      }
                      placeholder="#f9f9f9"
                      className="mt-2 w-full font-mono text-sm"
                      disabled={isPending}
                    />
                  </PopoverContent>
                </Popover>
                <span className="font-mono text-sm text-muted-foreground">
                  {formData.tableAlternatingColor || "#f9f9f9"}
                </span>
                {formData.tableAlternatingColor && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormData({ ...formData, tableAlternatingColor: "" })}
                    disabled={isPending}
                  >
                    {t.clear}
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{t.evenRowColorDescription}</p>
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button type="submit" loading={isPending}>
          {t.save}
        </Button>
      </div>
    </form>
  );
}
